import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';

interface LoadingAnimationProps {
  onComplete: () => void;
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [aiTextIndex, setAiTextIndex] = useState(0);
  const [showClickHint, setShowClickHint] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const vantaRef = useRef<HTMLDivElement>(null);
  const vantaEffect = useRef<any>(null);
  
  const { login, isAuthenticated } = useAuth();

  const aiTexts = [
    "Initializing AI Systems...",
    "Loading Neural Networks...",
    "Calibrating Language Models...",
    "Optimizing Translation Algorithms...",
    "Ready to Translate"
  ];

  const handleComplete = useCallback(() => {
    setLoginSuccess(true);
    // Vanta efektini temizle
    if (vantaEffect.current) {
      vantaEffect.current.destroy();
    }
    // Login başarılı olduğunda arkaplanı uygulama arkaplanına geçir
    setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onComplete();
      }, 300);
    }, 1000);
  }, [onComplete]);

  // Vanta.js NET animasyonunu başlat
  useEffect(() => {
    if (vantaRef.current && (window as any).VANTA) {
      vantaEffect.current = (window as any).VANTA.NET({
        el: vantaRef.current,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.00,
        minWidth: 200.00,
        scale: 1.00,
        scaleMobile: 1.00,
        color: 0x02c2ff,
        backgroundColor: 0x1a1a40,
        points: 14.00,
        maxDistance: 18.00,
        spacing: 12.00
      });
    }

    // Cleanup function
    return () => {
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    // If user is already authenticated, immediately complete
    if (isAuthenticated) {
      handleComplete();
      return;
    }
    
    // Show click hint immediately for non-authenticated users
    setShowClickHint(true);
  }, [isAuthenticated, handleComplete]);

  useEffect(() => {
    // Cycle through AI texts
    const textTimer = setInterval(() => {
      setAiTextIndex((prev) => (prev + 1) % aiTexts.length);
    }, 1200);

    return () => clearInterval(textTimer);
  }, [aiTexts.length]);

  const handleClick = () => {
    if (!showLoginForm) {
      setShowLoginForm(true);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(email, password);
      if (success) {
        handleComplete();
      } else {
        setError('Geçersiz email veya şifre. Lütfen bilgilerinizi kontrol edin.');
      }
    } catch (err) {
      setError('Giriş işlemi başarısız. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center cursor-pointer transition-all duration-800 overflow-hidden ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${
        loginSuccess ? 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800' : ''
      }`}
      onClick={handleClick}
    >
      {/* Vanta.js HALO Animation Background - Hide after login success */}
      {!loginSuccess && (
        <div 
          ref={vantaRef}
          className="absolute inset-0 w-full h-full"
          style={{ zIndex: 0 }}
        ></div>
      )}
                
      {/* Strong overlay to reduce animation visibility - Hide after login success */}
      {!loginSuccess && (
        <div className="absolute inset-0 bg-black/50" style={{ zIndex: 1 }}></div>
      )}

      {/* Dynamic Content Container - Hide after login success */}
      {!loginSuccess && (
        <div className="relative w-full h-full flex flex-col" style={{ zIndex: 10 }}>
          
          {/* Title Section - Animated */}
          <div className={`flex-1 flex items-center justify-center transition-all duration-1000 ${
            showLoginForm ? 'flex-none pt-20' : ''
          }`}>
            <div className="text-center">
              <h1 className="font-display text-8xl md:text-9xl font-extralight text-white tracking-tight">
                <span className="font-normal">Finar</span>
                <span className="ml-6 font-light">AI</span>
              </h1>
              
              {/* Click Hint - Only show when form is not visible */}
              {showClickHint && !showLoginForm && (
                <div className="mt-12">
                  <p className="font-ui text-lg text-white/60 animate-pulse font-medium">
                    Devam etmek için herhangi bir yere tıklayın
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Login Form Section - Animated from bottom */}
          {showLoginForm && (
            <div className="flex-1 flex items-center justify-center animate-slide-up">
              <div className="w-full max-w-md px-8">
                
                {/* Login Form */}
                <div className="backdrop-blur-xl rounded-2xl border border-white/30 p-8" 
                     style={{ background: 'rgba(255, 255, 255, 0.25)' }}>
                  
                  <div className="text-center mb-6">
                    <h2 className="font-display text-2xl font-medium text-white mb-2">
                      Tekrar Hoşgeldiniz
                    </h2>
                    <p className="font-body text-white/70">
                      Devam etmek için giriş yapın
                    </p>
                  </div>
                  
                  <form onSubmit={handleLogin} className="space-y-6">
                    {/* Email Field */}
                    <div>
                      <label className="block font-ui text-sm font-medium text-white/80 mb-2">
                        E-posta Adresi
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl backdrop-blur-lg border border-white/40 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/60 focus:border-white/60 transition-all duration-300"
                        style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                        placeholder="E-posta adresinizi girin"
                        required
                      />
                    </div>
                    
                    {/* Password Field */}
                    <div>
                      <label className="block font-ui text-sm font-medium text-white/80 mb-2">
                        Şifre
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-4 pr-12 py-3 rounded-xl backdrop-blur-lg border border-white/40 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/60 focus:border-white/60 transition-all duration-300"
                          style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                          placeholder="Şifrenizi girin"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/90 transition-colors"
                        >
                          {showPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="backdrop-blur-md rounded-xl border border-red-400/30 p-3 text-red-300 text-sm font-ui"
                           style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                        {error}
                      </div>
                    )}


                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full backdrop-blur-lg rounded-xl border border-white/40 py-3 font-ui font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/60 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'rgba(255, 255, 255, 0.25)' }}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          Giriş yapılıyor...
                        </div>
                      ) : (
                        'Giriş Yap'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
          
        </div>
      )}

      {/* Login Success State - Show loading message with app background */}
      {loginSuccess && (
        <div className="relative w-full h-full flex items-center justify-center" style={{ zIndex: 10 }}>
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-300 dark:border-gray-600 border-t-[#02c2ff] rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-600 dark:text-gray-300">
              Uygulamaya yönlendiriliyor...
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default LoadingAnimation;
